import { GlobalConfigType, setLoanGroupData } from 'actions'
import cloneDeep from 'clone-deep'
import { type InputType, FieldOrigin, IVisibleProp, LoanFieldProps } from 'config'
import {
  calcCLTVFields,
  calcLTVFields,
  convertValueToLabel,
  dscrRelatedFields,
  editableFieldOptions,
  editableOptionFields,
  fieldsByTransactionTypeAndProductTypeConstant,
  getInitialLoanFields,
  getMappedProductType,
  loanCustomRangeOptionFields,
  loanCustomSelectOptionFields,
  loanOptionsConstants,
  MINVALUE,
  MultiPropertyFields,
  priceLoanRelatedFields,
  ProductTypes,
  propertyTypesVacantNeeded,
} from 'config/loan.constants'
import { isCustomFieldKey } from 'config/loan.input.fields.constants'
import { propertyStateSelectOptions } from 'config/propertyStateOptions'
import { DateTime } from 'luxon'
import type {
  ILoanGlobalSettings,
  IManualPricingSetting,
  IManualProgram,
  IMinMaxLimitPerTypes,
} from 'pages/Admin/AdminTools/Configuration/GlobalSettings'
import { convertSelectRangeOptionsToMap } from 'pages/Admin/AdminTools/Configuration/LoanFieldsProperties/utils'
import {
  closingCostEstimateIsSameFeeWorksheet,
  isSameStructureApplicationCreditScore,
} from 'pages/LoanApplication/CreditScore/logic'
import { ILtvValues, IRange } from 'pages/Resources'
import { convertLTV } from 'pages/Resources/GuidelineOverlay/logic'
import { IUnderwritingFee } from 'pages/Resources/UnderwritingFee'
import { createElement } from 'react'
import { toast } from 'react-toastify'
import { store } from 'reducers'
import { combinedMonthlyExpense, combinedPropertyMonthlyExpense } from 'services/pdfs/utils'
import { ISelectOption } from 'stories/components/SelectOptionInputs/types'
import {
  formatDate,
  fromToNumberOptions,
  getPrice2decimal,
  getPrice3decimal,
  InputConvert,
  InputValidate,
  isBeforeOrEqualToday,
  isEmpty,
  removeComma,
  REQUIRED_FIELD_ERROR_MESSAGE,
  solveDecimalJavascriptSum,
  thousandSeperator,
} from 'utils'
import {
  getAdditionalPropertyFieldProps,
  getAdditionalPropertyInvisibleFields,
  getInvisibleFields,
  isVisibleFieldVisibleProps,
  isVisiblePropertyFieldVisibleProps,
  removeAdditionalPropertyInvisibleFields,
  removeInvisibleFields,
} from 'utils/loan'

import { priceAdjustmentKeysWithMinMax } from './constant'
import type {
  ILoan,
  ILoanDetail,
  ILoanDetailChangedJson,
  ILoanProcess,
  ILockDay,
  ILockTermPrice,
  IPrice,
  IPrice_Program_Price,
  IPriceAdjustment,
  IProduct,
  IProgram,
  IPropertyChangedJson,
} from './interfaces'

export const analyzeLoanStructureValue = (_loan: ILoan, isSummary = true, overrideFields: Record<string, any> = {}) => {
  const visibleFields = visibleLoansLogic(_loan)
  const loan = Object.assign({}, _loan, overrideFields)
  Object.keys(overrideFields).forEach((key) => {
    loan[key] = overrideFields[key]
  })

  let propertyPurchasePrice = visibleFields.includes('propertyPurchasePrice')
    ? removeComma(loan.propertyPurchasePrice)
    : 0
  let asIsValue = visibleFields.includes('asIsValue') ? removeComma(loan.asIsValue) : 0
  let constructionReserve = visibleFields.includes('constructionReserve') ? removeComma(loan.constructionReserve) : 0
  let proposedLoanAmount = visibleFields.includes('proposedLoanAmount') ? removeComma(loan.proposedLoanAmount) : 0
  let aPieceAmount = visibleFields.includes('aPieceAmount') ? removeComma(loan.aPieceAmount) : 0
  let afterRepairValue = visibleFields.includes('afterRepairValue') ? removeComma(loan.afterRepairValue) : 0
  let rehabBudget = visibleFields.includes('rehabBudget') ? removeComma(loan.rehabBudget) : 0
  let lienPayoff = visibleFields.includes('lienPayoff') ? removeComma(loan.lienPayoff) : 0
  let closingCostEstimate = visibleFields.includes('closingCostEstimate') ? removeComma(loan.closingCostEstimate) : 0
  let interestReserve = visibleFields.includes('interestReserve') ? removeComma(loan.interestReserve) : 0
  let secondLien = visibleFields.includes('secondLien') ? removeComma(loan.secondLien) : 0
  let secondLienPayment =
    secondLien > 0 && visibleFields.includes('secondLienPayment') ? removeComma(loan.secondLienPayment) : 0
  let proposedMonthlyInsurance = removeComma(loan.proposedMonthlyInsurance)
  let proposedMonthlyTaxes = removeComma(loan.proposedMonthlyTaxes)
  let proposedMonthlyHoaDues = removeComma(loan.proposedMonthlyHoaDues)
  let proposedMonthlyRent = removeComma(loan.proposedMonthlyRent)
  let floodInsurance = removeComma(loan.floodInsurance)
  let schoolTax = removeComma(loan.schoolTax)
  let otherTax = removeComma(loan.otherTax)
  if (visibleFields.includes('isBPiece') && loan.isBPiece) proposedLoanAmount = aPieceAmount

  let minPurchasePriceAppraisedValue = asIsValue
  if (loan.transactionType === 'Purchase') minPurchasePriceAppraisedValue = Math.min(propertyPurchasePrice, asIsValue)

  if (isSummary && loan.isMultipleProperties) {
    const { multipleProperties } = loan
    multipleProperties?.forEach((item, index) => {
      const propertyVisibleFields = visiblePropertiesLogic(index, item)
      if (propertyVisibleFields.includes('propertyPurchasePrice'))
        propertyPurchasePrice += removeComma(item.propertyPurchasePrice)
      if (propertyVisibleFields.includes('asIsValue')) asIsValue += removeComma(item.asIsValue)
      if (propertyVisibleFields.includes('afterRepairValue')) afterRepairValue += removeComma(item?.afterRepairValue)
      if (propertyVisibleFields.includes('rehabBudget')) rehabBudget += removeComma(item?.rehabBudget)
      if (propertyVisibleFields.includes('constructionReserve'))
        constructionReserve += removeComma(item?.constructionReserve)
      if (propertyVisibleFields.includes('lienPayoff')) lienPayoff += removeComma(item.lienPayoff)
      if (propertyVisibleFields.includes('secondLien')) {
        secondLien += removeComma(item.secondLien)
        if (removeComma(item.secondLien) > 0 && propertyVisibleFields.includes('secondLienPayment'))
          secondLienPayment += removeComma(item.secondLienPayment)
      }
      proposedMonthlyInsurance += removeComma(item.proposedMonthlyInsurance)
      proposedMonthlyTaxes += removeComma(item.proposedMonthlyTaxes)
      proposedMonthlyHoaDues += removeComma(item.proposedMonthlyHoaDues)
      proposedMonthlyRent += removeComma(item.proposedMonthlyRent)
      floodInsurance += removeComma(item.floodInsurance)
      schoolTax += removeComma(item.schoolTax)
      otherTax += removeComma(item.otherTax)
      const minValue =
        item.transactionType === 'Purchase'
          ? Math.min(removeComma(item.propertyPurchasePrice), removeComma(item.asIsValue))
          : removeComma(item.asIsValue)
      minPurchasePriceAppraisedValue += minValue
    })
  }

  return {
    propertyPurchasePrice,
    asIsValue,
    proposedLoanAmount,
    afterRepairValue,
    rehabBudget,
    constructionReserve,
    lienPayoff,
    closingCostEstimate,
    interestReserve,
    secondLien,
    secondLienPayment,
    proposedMonthlyInsurance,
    proposedMonthlyTaxes,
    proposedMonthlyHoaDues,
    proposedMonthlyRent,
    floodInsurance,
    schoolTax,
    otherTax,
    minPurchasePriceAppraisedValue,
  }
}

export const getLockExpired = (data: any) => {
  let rlt = false

  if (data.rateLocked && data.expireDate) {
    const expireDate = DateTime.fromJSDate(new Date(data.expireDate)).startOf('day')
    if (expireDate.isValid) rlt = !isBeforeOrEqualToday(data.expireDate)
    else console.error('Invalid expireDate:', data.expireDate)
  }

  return rlt
}

export const isNonDutchInterestType = (interestType: string) => {
  return interestType === 'Non Dutch'
}

/**
 * A Non-Dutch loan that finances its interest reserve into principal at closing, so the reserve
 * accrues interest from day one the way a Dutch loan's does. Only the reserve is affected: the
 * construction holdback keeps Non-Dutch treatment, accruing draw by draw as it is disbursed.
 * Mirrors `chargesInterestOnReserve` in the backend's utils/interest-reserve.
 */
export const chargesInterestOnReserve = (loan: any): boolean =>
  isNonDutchInterestType(loan?.interestType || '') && !!loan?.chargeInterestOnReserve

/**
 * Whether the interest reserve is held out of principal until the Interest Reserve Ledger advances
 * it. True for a Non-Dutch loan unless it charges interest on the reserve.
 */
export const holdsOutInterestReserve = (loan: any): boolean =>
  isNonDutchInterestType(loan?.interestType || '') && !chargesInterestOnReserve(loan)

/** Our loan plus the senior debt standing ahead of it, i.e. what CLTV is measured on. */
export const calcTotalCombinedDebt = (loan: any): number =>
  removeComma(loan?.proposedLoanAmount) + removeComma(loan?.secondLien)

export const calculateLoanLimits = (
  loan: ILoan | null = null,
  getMaximumLoanAmount: boolean = false,
  maxLimit: any = null,
  overrideFields: Record<string, any> = {},
) => {
  let rlt: any = {
    Limit: {},
  }
  try {
    const state = store.getState()
    let ltvMaxLimit: any = state.ltvMaxLimit
    if (maxLimit !== null) ltvMaxLimit = maxLimit

    if (loan === null) loan = state.loan as ILoan
    const loanGlobalSettings = state.loanDetail.loanGlobalSettings as ILoanGlobalSettings
    const loanFieldProperties = state.globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
    const fieldProperties = (loanFieldProperties?.[FieldOrigin.LoanStructure] || {}) as Record<string, IVisibleProp>
    const ltcFormulas = loanGlobalSettings?.ltcFormulas || []
    const mappedProductType = getMappedProductType(loan?.productType, fieldProperties)
    const ltcFormula = ltcFormulas.find((formula) => formula.productType === mappedProductType)

    const { productType, transactionType } = loan

    let _aiv_ltv = 0
    let _arv_ltv = 0
    let _ltc = 0
    let _ltp = 0
    let _cltv = 0

    const visibleFields =
      fieldsByTransactionTypeAndProductTypeConstant[
        getMappedProductType(productType, fieldProperties) as ProductTypes
      ]?.[transactionType] || []

    const {
      propertyPurchasePrice,
      constructionReserve,
      proposedLoanAmount,
      afterRepairValue,
      rehabBudget,
      lienPayoff,
      closingCostEstimate,
      interestReserve,
      minPurchasePriceAppraisedValue,
      secondLien,
    } = analyzeLoanStructureValue(loan, true, overrideFields)
    const totalLoanAmount = removeComma(loan.proposedLoanAmount)
    _aiv_ltv =
      (100 * (proposedLoanAmount - constructionReserve - (holdsOutInterestReserve(loan) ? interestReserve : 0))) /
      minPurchasePriceAppraisedValue
    _arv_ltv = (100 * proposedLoanAmount) / afterRepairValue
    if (ltcFormula) {
      const {
        loanAmountFactor,
        constructionBudgetFactor,
        interestReserveFactor,
        loanAmountPercentageFactor,
        fixedAmount,
      } = ltcFormula.formula

      _ltc =
        (100 * proposedLoanAmount * loanAmountFactor) /
        (minPurchasePriceAppraisedValue +
          rehabBudget * constructionBudgetFactor +
          (loan.includeInterestReserve ? interestReserve * interestReserveFactor : 0) +
          (proposedLoanAmount * loanAmountPercentageFactor) / 100 +
          fixedAmount)
    } else
      _ltc =
        (100 * proposedLoanAmount) /
        (minPurchasePriceAppraisedValue + rehabBudget + (loan.includeInterestReserve ? interestReserve : 0))
    _ltp = (100 * (proposedLoanAmount - constructionReserve)) / propertyPurchasePrice
    _cltv = (100 * (totalLoanAmount + secondLien - constructionReserve)) / minPurchasePriceAppraisedValue

    rlt.Limit.aiv_ltv = Number(_aiv_ltv.toFixed(3))
    rlt.Limit.arv_ltv = Number(_arv_ltv.toFixed(3))
    rlt.Limit.ltc = Number(_ltc.toFixed(3))
    rlt.Limit.ltp = Number(_ltp.toFixed(3))
    rlt.Limit.cltv = Number(_cltv.toFixed(3))

    // Calculate B Piece limits if applicable
    if (visibleFields.includes('isBPiece') && loan.isBPiece) {
      const totalLoanAmount = removeComma(loan.proposedLoanAmount)

      let _b_piece_aiv_ltv =
        (100 * (totalLoanAmount - constructionReserve - (holdsOutInterestReserve(loan) ? interestReserve : 0))) /
        minPurchasePriceAppraisedValue
      let _b_piece_arv_ltv = (100 * totalLoanAmount) / afterRepairValue
      let _b_piece_ltc = 0
      if (ltcFormula) {
        const {
          loanAmountFactor,
          constructionBudgetFactor,
          interestReserveFactor,
          loanAmountPercentageFactor,
          fixedAmount,
        } = ltcFormula.formula

        _b_piece_ltc =
          (100 * totalLoanAmount * loanAmountFactor) /
          (minPurchasePriceAppraisedValue +
            rehabBudget * constructionBudgetFactor +
            (loan.includeInterestReserve ? interestReserve * interestReserveFactor : 0) +
            (totalLoanAmount * loanAmountPercentageFactor) / 100 +
            fixedAmount)
      } else
        _b_piece_ltc =
          (100 * totalLoanAmount) /
          (minPurchasePriceAppraisedValue + rehabBudget + (loan.includeInterestReserve ? interestReserve : 0))
      let _b_piece_ltp = (100 * (totalLoanAmount - constructionReserve)) / propertyPurchasePrice

      rlt.Limit.b_piece_aiv_ltv = Number(_b_piece_aiv_ltv.toFixed(3))
      rlt.Limit.b_piece_arv_ltv = Number(_b_piece_arv_ltv.toFixed(3))
      rlt.Limit.b_piece_ltc = Number(_b_piece_ltc.toFixed(3))
      rlt.Limit.b_piece_ltp = Number(_b_piece_ltp.toFixed(3))
    }

    try {
      rlt.Limit.max_aiv_ltv = ltvMaxLimit.max_aiv_ltv
      rlt.Limit.max_arv_ltv = ltvMaxLimit.max_arv_ltv
      rlt.Limit.max_ltc = ltvMaxLimit.max_ltc
      rlt.Limit.max_ltp = ltvMaxLimit.max_ltp
      rlt.Limit.max_cltv = ltvMaxLimit?.max_cltv
    } catch {}

    if (getMaximumLoanAmount) {
      let minValue = MINVALUE
      if (rlt.Limit.max_aiv_ltv) {
        minValue = Math.min(
          minValue,
          removeComma((rlt.Limit.max_aiv_ltv * minPurchasePriceAppraisedValue) / 100 + constructionReserve),
        )
      }
      if (rlt.Limit.max_arv_ltv) {
        minValue = Math.min(minValue, removeComma((rlt.Limit.max_arv_ltv * afterRepairValue) / 100))
      }
      if (rlt.Limit.max_ltc) {
        minValue = Math.min(
          minValue,
          removeComma((rlt.Limit.max_ltc * (minPurchasePriceAppraisedValue + rehabBudget)) / 100),
        )
      }
      if (rlt.Limit.max_ltp) {
        minValue = Math.min(
          minValue,
          removeComma((rlt.Limit.max_ltp * propertyPurchasePrice) / 100 + constructionReserve),
        )
      }
      if (transactionType === 'Refinance') {
        if (visibleFields.indexOf('rehabBudget') !== -1) {
          minValue = Math.min(
            minValue,
            rehabBudget + rehabBudget * 0.2 + lienPayoff + closingCostEstimate + interestReserve,
          )
        }
      }
      rlt.Limit = Math.trunc(minValue)
    }
  } catch {}
  return rlt.Limit
}

export const calculateLTV = (loan: ILoan | null = null, cltv: boolean = false, isBPiece: boolean = false) => {
  let rlt: any = ''
  try {
    if (loan === null) {
      const state = store.getState()
      loan = state.loan as ILoan
    }

    let { constructionReserve, proposedLoanAmount, secondLien, minPurchasePriceAppraisedValue, interestReserve } =
      analyzeLoanStructureValue(loan)
    if (isBPiece) proposedLoanAmount = removeComma(loan.proposedLoanAmount) - removeComma(loan.aPieceAmount)

    rlt =
      (100 * (proposedLoanAmount - constructionReserve - (holdsOutInterestReserve(loan) ? interestReserve : 0))) /
      minPurchasePriceAppraisedValue
    if (cltv) {
      const visibleFields = visibleLoansLogic(loan)
      const totalLoanAmount = removeComma(loan.proposedLoanAmount)
      const amount = visibleFields.includes('constructionReserve')
        ? totalLoanAmount + secondLien - constructionReserve
        : totalLoanAmount + secondLien
      rlt = (100 * amount) / minPurchasePriceAppraisedValue
    }

    return rlt.toFixed(3)
  } catch {}
  return rlt
}

export const calculateBPieceLTV = (loan: ILoan | null = null, isBPiece: boolean = false) => {
  if (loan === null) {
    const state = store.getState()
    loan = state.loan as ILoan
  }

  const proposedLoanAmount = removeComma(loan.proposedLoanAmount)
  const aPieceAmount = removeComma(loan.aPieceAmount)
  const bPieceAmount = proposedLoanAmount - aPieceAmount
  return Number(
    isBPiece
      ? ((100 * bPieceAmount) / proposedLoanAmount).toFixed(3)
      : ((100 * aPieceAmount) / proposedLoanAmount).toFixed(3),
  )
}

export const calculateBPiece = (loan: ILoan) => {
  return thousandSeperator(removeComma(loan.proposedLoanAmount) - removeComma(loan.aPieceAmount))
}

export const calculateLoanAmount = (loan: ILoan | null = null, LTV: number) => {
  let rlt: any = ''
  try {
    if (loan === null) {
      const state = store.getState()
      loan = state.loan as ILoan
    }

    const { constructionReserve, minPurchasePriceAppraisedValue, interestReserve } = analyzeLoanStructureValue(loan)

    rlt =
      (LTV * minPurchasePriceAppraisedValue) / 100 +
      constructionReserve +
      (holdsOutInterestReserve(loan) ? interestReserve : 0)

    return Math.floor(rlt)
  } catch {}
  return rlt
}

export const calculateAPieceLoanAmount = (loan: ILoan | null = null, LTV: number) => {
  if (loan === null) {
    const state = store.getState()
    loan = state.loan as ILoan
  }

  const proposedLoanAmount = removeComma(loan.proposedLoanAmount)
  return Math.floor((proposedLoanAmount * LTV) / 100)
}

export const calculateLTC = (loan: ILoan | null = null) => {
  let rlt: any = ''
  try {
    const state = store.getState()
    if (loan === null) loan = state.loan as ILoan
    const loanGlobalSettings = state.loanDetail.loanGlobalSettings as ILoanGlobalSettings
    const loanFieldProperties = state.globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
    const fieldProperties = (loanFieldProperties?.[FieldOrigin.LoanStructure] || {}) as Record<string, IVisibleProp>
    const ltcFormulas = loanGlobalSettings?.ltcFormulas || []
    const mappedProductType = getMappedProductType(loan?.productType, fieldProperties)
    const ltcFormula = ltcFormulas.find((formula) => formula.productType === mappedProductType)

    const { minPurchasePriceAppraisedValue, rehabBudget, interestReserve } = analyzeLoanStructureValue(loan)
    const totalLoanAmount = removeComma(loan.proposedLoanAmount)

    if (ltcFormula) {
      const {
        loanAmountFactor,
        constructionBudgetFactor,
        interestReserveFactor,
        loanAmountPercentageFactor,
        fixedAmount,
      } = ltcFormula.formula

      rlt =
        (100 * totalLoanAmount * loanAmountFactor) /
        (minPurchasePriceAppraisedValue +
          rehabBudget * constructionBudgetFactor +
          (loan.includeInterestReserve ? interestReserve * interestReserveFactor : 0) +
          (totalLoanAmount * loanAmountPercentageFactor) / 100 +
          fixedAmount)
    } else {
      rlt =
        (100 * totalLoanAmount) /
        (minPurchasePriceAppraisedValue + rehabBudget + (loan.includeInterestReserve ? interestReserve : 0))
    }

    return rlt.toFixed(3)
  } catch {}
  return rlt
}

/**
 * Returns computed display values for all calculated LoanStructure fields.
 * When admins link a field on another page (e.g. PropertyInformation) to one
 * of these LoanStructure keys, the receiving page calls this function to
 * resolve the live calculated value instead of relying on loan state storage.
 *
 * To expose additional calculated fields for linking, add them here.
 */
export const getLoanStructureCalculatedValues = (loan: ILoan | null = null): Record<string, string> => {
  if (loan === null) {
    const state = store.getState()
    loan = state.loan as ILoan
  }
  return {
    ltv: convertLTV(calculateLTV(loan)),
    cltv: convertLTV(calculateLTV(loan, true)),
    ltc: convertLTV(calculateLTC(loan)),
  }
}

export const calculateLoanAmountFromLTC = (loan: ILoan | null = null, LTC: number) => {
  let rlt: any = ''
  try {
    if (loan === null) {
      const state = store.getState()
      loan = state.loan as ILoan
    }

    const { minPurchasePriceAppraisedValue, rehabBudget } = analyzeLoanStructureValue(loan)

    rlt = (LTC * (minPurchasePriceAppraisedValue + rehabBudget)) / 100

    return Math.floor(rlt)
  } catch {}
  return rlt
}

export const visibleLoansLogic = (loan?: ILoan): string[] => {
  const state = store.getState()
  if (!loan) {
    loan = state.loan as ILoan
  }
  const { globalConfigReducer } = state
  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
  const fieldProps = loanFieldProperties[FieldOrigin.LoanStructure]
  let visibleFields = ['productType', 'transactionType']
  if (loan['productType'] && loan['transactionType']) {
    const mappedProductType = getMappedProductType(loan['productType'], fieldProps)
    const definedVisibleFields =
      fieldsByTransactionTypeAndProductTypeConstant[mappedProductType as ProductTypes]?.[loan['transactionType']] || []
    visibleFields = [...visibleFields, ...definedVisibleFields]
    if (loan['propertyType'] === 'Condo') visibleFields.push('condoType')
    if (propertyTypesVacantNeeded.includes(loan.propertyType)) visibleFields.push('vacant')
    else visibleFields = [...visibleFields.filter((item) => item !== 'vacant')]
    if (loan['propertyState'] === 'NY' && loan['transactionType'] === 'Refinance')
      visibleFields.push('isCEMATransaction')
    // The senior-debt block only means anything once there is a senior lien to describe, so it
    // follows the same rule the Additional Lien Payment always has.
    if (removeComma(loan['secondLien']) > 0)
      visibleFields.push('secondLienPayment', 'existingLienholder', 'totalCombinedDebt')

    // Only a Non-Dutch loan has a choice to make about the reserve: a Dutch loan finances it in and
    // charges interest on it regardless.
    if (definedVisibleFields.includes('interestReserve') && isNonDutchInterestType(loan['interestType'] || ''))
      visibleFields.push('chargeInterestOnReserve')
    const newVisibleInputs = Object.entries(fieldProps)
      .filter(([key, prop]) => isCustomFieldKey(key) || !!(prop as IVisibleProp).visibleLogic?.length)
      .map(([key]) => key)
    visibleFields.push(...newVisibleInputs)
  }
  visibleFields = removeInvisibleFields(loan, FieldOrigin.LoanStructure, visibleFields)
  if (loan.isMultipleProperties) visibleFields.push('summaryOfAllProperties')
  return visibleFields
}

export const visiblePropertiesLogic = (index: number, propertyData?: any): string[] => {
  const { loan, globalConfigReducer } = store.getState()
  if (!propertyData) propertyData = loan.multipleProperties[index]

  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps

  const fieldProps = getAdditionalPropertyFieldProps(loanFieldProperties)

  let visibleFields: string[] = ['transactionType']
  if (loan['productType'] && propertyData['transactionType']) {
    const mappedProductType = getMappedProductType(
      loan['productType'],
      loanFieldProperties?.[FieldOrigin.LoanStructure],
    )
    const definedVisibleFields =
      fieldsByTransactionTypeAndProductTypeConstant[mappedProductType as ProductTypes]?.[
        propertyData['transactionType']
      ] || []

    visibleFields = [...visibleFields, ...definedVisibleFields]

    if (propertyData['propertyType'] === 'Condo') visibleFields.push('condoType')
    if (propertyTypesVacantNeeded.includes(propertyData.propertyType)) visibleFields.push('vacant')
    else visibleFields = [...visibleFields.filter((item) => item !== 'vacant')]
    if (removeComma(propertyData['secondLien']) > 0) visibleFields.push('secondLienPayment')

    const newVisibleInputs = Object.entries(fieldProps)
      .filter(([key, prop]) => isCustomFieldKey(key) || !!(prop as IVisibleProp).visibleLogic?.length)
      .map(([key]) => key)
    visibleFields.push(...newVisibleInputs)
  }
  visibleFields = removeAdditionalPropertyInvisibleFields(index, propertyData, visibleFields)
  return visibleFields
}

export const propertyTypeNumberUnitInputLogic = (propertyType: string, input: any) => {
  switch (propertyType) {
    case 'SFR-Detached':
    case 'SFR-Attached':
    case 'Industrial':
      input.inputType = 'text'
      input.value = 1
      input.readOnly = true
      break
    case 'PUD':
    case 'Automotive':
    case 'Modular':
    case 'Manufactured':
      input.inputType = 'select'
      input.options = fromToNumberOptions(1, 4)
      input.hasDefaultOption = true
      input.readOnly = false
      break
    case 'Office':
    case 'Warehouse':
      input.inputType = 'select'
      input.options = fromToNumberOptions(1, 100)
      input.hasDefaultOption = true
      input.readOnly = false
      break
    case '2-4 Units':
      input.inputType = 'select'
      input.options = fromToNumberOptions(2, 4)
      input.hasDefaultOption = true
      input.readOnly = false
      break

    default: {
      input.inputType = 'text'
      input.readOnly = false
      break
    }
  }
  return input
}

export const loanFieldsValidateAll = (
  isNew: boolean,
  fieldData: any,
  loanGlobalSettings: ILoanGlobalSettings,
  fieldProperties: Record<string, IVisibleProp>,
  isPool: boolean,
  loan?: any,
) => {
  const loanField = cloneDeep(fieldData)
  const { loan: loanData, loanDetail } = store.getState()
  if (!loan) loan = loanData

  Object.keys(fieldProperties)
    .filter((key) => isCustomFieldKey(key))
    .forEach((key) => {
      loanField[key] = { ...fieldProperties[key] } as any
    })
  loanField.numberOfUnits = propertyTypeNumberUnitInputLogic(loan.propertyType, loanField.numberOfUnits)
  const experienceRange = getRangeFromString(loan.experience)
  let { from, to } = experienceRange
  if (from === null) from = 0
  if (to === null) to = 100
  loanField.howManyExperiences.options = fromToNumberOptions(from, to)

  const invisibleFields = getInvisibleFields(FieldOrigin.LoanStructure, loan)

  Object.keys(loanField).map((key) => {
    let options = fieldProperties[key]?.options as any
    if (options && Array.isArray(options)) {
      try {
        options = options.filter((option: any) => {
          if (!option.visibleLogic) return true
          const isVisible = isVisibleFieldVisibleProps({ visibleLogic: option.visibleLogic }, loan)
          return isVisible
        }) as any
      } catch (e) {
        options = []
      }
    }

    if (key === 'loanTerm') {
      const loanTermOption = convertSelectRangeOptionsToMap(options)
      loanField[key].options = !isEmpty(loanTermOption)
        ? loanTermOption
        : isNonDSCRProduct(loan.productType, fieldProperties)
        ? loanOptionsConstants.loanTerm
        : loanOptionsConstants.dscrLoanTerm
    }

    if (key === 'propertyState') {
      loanField[key].options = propertyStateSelectOptions(
        fieldProperties[key]?.options as ISelectOption[] | undefined,
        loan?.propertyState,
      )
    }

    if (
      fieldProperties[key] &&
      !!fieldProperties[key]?.options &&
      !!options?.length &&
      [...loanCustomRangeOptionFields, ...loanCustomSelectOptionFields].includes(key)
    ) {
      ;(loanField[key] as any).options = convertSelectRangeOptionsToMap(options)
    }
    if (fieldProperties[key] && editableOptionFields.includes(key) && fieldProperties[key]?.options?.length) {
      const visibleKeys: Record<string, any> = {}
      ;(fieldProperties[key].options as ISelectOption[])
        .filter(
          (opt) =>
            opt.visible !== false &&
            (!opt.visibleLogic || isVisibleFieldVisibleProps({ visibleLogic: opt.visibleLogic }, loan)),
        )
        .forEach((opt) => (visibleKeys[opt.key] = opt.title))
      if (Object.keys(visibleKeys).length) (loanField[key] as any).options = visibleKeys
    }

    loanField[key].title =
      fieldProperties[key] && fieldProperties[key].title ? fieldProperties[key].title || '' : loanField[key].title
    loanField[key].type = (
      fieldProperties[key] && fieldProperties[key].type ? fieldProperties[key].type : loanField[key].type
    ) as any

    if (fieldProperties?.[key]?.required !== undefined) loanField[key].required = fieldProperties[key].required

    if (invisibleFields.includes(key)) {
      loanField[key].error = ''
      return
    }

    loanField[key].error = InputValidate({ ...loanField[key], value: loan[key] }, loanDetail, loan)
  })

  //  The After Repair Value (ARV) must be no less than setting% of the total costs (lesser of the as is value / purchase price + rehab budget)
  let { afterRepairValue, rehabBudget, minPurchasePriceAppraisedValue } = analyzeLoanStructureValue(loan)
  const minAfterRepairValue = Math.ceil(
    ((100 + Number(loanGlobalSettings.minAfterRepairValue)) * (minPurchasePriceAppraisedValue + rehabBudget)) / 100,
  )
  if (!invisibleFields.includes('afterRepairValue') && 0 < afterRepairValue && afterRepairValue < minAfterRepairValue) {
    loanField['afterRepairValue'].error = `The After Repair Value does not confirm at least ${
      loanGlobalSettings.minAfterRepairValue
    }% profitability due to it being less than $${getPrice2decimal(minAfterRepairValue)}`
  }

  if (!isNew && !invisibleFields.includes('closingCostEstimate')) {
    const sameEstimate = closingCostEstimateIsSameFeeWorksheet()
    if (!sameEstimate.same) loanField.closingCostEstimate.error = sameEstimate.error
  }

  const sameCredit = isSameStructureApplicationCreditScore()
  if (!invisibleFields.includes('estimatedCreditScore') && !sameCredit.same && sameCredit.value !== 0)
    loanField['estimatedCreditScore'].error = sameCredit.error

  const vacantError = getVacantUnitsError(loan.vacant, loan.vacantIsPercentage, loan.numberOfUnits)
  if (!invisibleFields.includes('vacant') && vacantError && loanField.vacant) loanField.vacant.error = vacantError

  if (isPool || isNew) loanField['closingCostEstimate'].readOnly = false

  const loanAmountInputType = loanGlobalSettings?.allowCentForLoanAmount ? 'thousandSep' : 'thousandSepNoDecimal'
  loanField['proposedLoanAmount'].type = loanAmountInputType
  loanField['aPieceAmount'].type = loanAmountInputType

  return loanField
}

export const multiPropertyFieldsValidateAll = (index: number, loanField: any, loanData?: ILoan) => {
  const { loan, loanDetail, globalConfigReducer } = store.getState()
  if (!loanData) loanData = loan

  if (!loanData) return loanField
  const targetData = loanData.multipleProperties[index]

  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps

  const fieldProperties = getAdditionalPropertyFieldProps(loanFieldProperties)
  // A property entered here offers the same renamed choices as the loan's own fields, which are
  // the ones configured on Loan Structure.
  const masterOptionProps = (loanFieldProperties?.[FieldOrigin.LoanStructure] || {}) as Record<string, IVisibleProp>
  const invisibleFields = getAdditionalPropertyInvisibleFields(index, targetData)

  loanField.numberOfUnits = propertyTypeNumberUnitInputLogic(targetData.propertyType, loanField.numberOfUnits)

  Object.keys(fieldProperties)
    .filter((key) => isCustomFieldKey(key) && !!fieldProperties[key]?.linkedField)
    .forEach((key) => {
      loanField[key] = fieldProperties[key]
    })

  Object.keys(loanField).map((key) => {
    if (
      !!(targetData as any)[key] &&
      fieldProperties[key] &&
      !!fieldProperties[key]?.options &&
      !!fieldProperties[key].options?.length &&
      [...loanCustomRangeOptionFields, ...loanCustomSelectOptionFields].includes(key)
    ) {
      ;(loanField[key] as any).options = convertSelectRangeOptionsToMap(fieldProperties[key]?.options || [])
    }
    const renamableOptions = editableFieldOptions<any>(key, fieldProperties, masterOptionProps)
    if (renamableOptions) {
      const visibleKeys = renamableOptions
        .filter((opt) => {
          const isOptionEnabled = opt.visible !== false

          if (!isOptionEnabled) {
            return false
          }

          return isVisiblePropertyFieldVisibleProps(index, { visibleLogic: opt.visibleLogic }, loan)
        })
        .map((opt) => opt.key)
      if (visibleKeys.length) {
        const labels: Record<string, string> = {}
        renamableOptions.forEach((opt) => {
          if (visibleKeys.includes(opt.key)) labels[opt.key] = opt.title || opt.key
        })
        ;(loanField[key] as any).options = labels
      }
    }
    loanField[key].title =
      fieldProperties[key] && fieldProperties[key].title ? fieldProperties[key].title || '' : loanField[key].title
    loanField[key].type = (
      fieldProperties[key] && fieldProperties[key].type ? fieldProperties[key].type : loanField[key].type
    ) as any
    if (fieldProperties?.[key]?.required !== undefined) loanField[key].required = fieldProperties[key].required
    if (invisibleFields.includes(key)) {
      loanField[key].error = ''
      return
    }
    loanField[key].error = InputValidate({ ...loanField[key], value: (targetData as any)[key] }, loanDetail, loan)
  })

  const vacantError = getVacantUnitsError(targetData.vacant, targetData.vacantIsPercentage, targetData.numberOfUnits)
  if (!invisibleFields.includes('vacant') && vacantError && loanField.vacant) loanField.vacant.error = vacantError

  return loanField
}

export const showLimitSection = () => {
  let flag = true
  const { loan } = store.getState()
  if (!isNonDSCRProduct(loan['productType'])) flag = false
  return flag
}

export const loanDataConvert = (fields: Record<string, InputType>) => {
  try {
    const { loan } = store.getState()
    const data: any = {}
    Object.keys(fields).map((key) => {
      data[key] = InputConvert(fields[key], loan[key])
    })
    store.dispatch(setLoanGroupData(data))
    const key2 = 'propertyCounty'
    data[key2] = loan[key2]
    return data
  } catch (e) {
    return {}
  }
}

const buildMinimumValueTooltip = (items: { label: string; value: number }[]) =>
  createElement(
    'div',
    null,
    createElement('div', { className: 'font-bold mb-2' }, 'The minimum value of the following:'),
    ...items.map((item, index) =>
      createElement(
        'div',
        { key: index, className: 'mb-1' },
        `${item.label} = `,
        createElement('span', { className: 'text-[14px]' }, getPrice3decimal(item.value)),
      ),
    ),
  )

const multiplyPrecisely = (value: number, multiplier: number) => Number((value * multiplier).toFixed(10))

export const getMaximumTotalLoanAmountTip = (
  loan: ILoan | null,
  limit: any,
  maxLoanAmount: number,
  loanGlobalSettings: ILoanGlobalSettings,
  totalLtvLimits?: ILtvValues,
) => {
  if (loan === null) {
    const state = store.getState()
    loan = state.loan as ILoan
  }
  const visibleFields = visibleLoansLogic(loan)

  const {
    propertyPurchasePrice,
    constructionReserve,
    afterRepairValue,
    rehabBudget,
    lienPayoff,
    closingCostEstimate,
    interestReserve,
    minPurchasePriceAppraisedValue,
    proposedLoanAmount,
  } = analyzeLoanStructureValue(loan)
  // const totalLoanAmount = removeComma(loan.proposedLoanAmount)

  const lists = []
  let AIVLTVMIN = -1
  let minValue = MINVALUE
  if (loan.transactionType === 'Refinance') {
    if (visibleFields.includes('rehabBudget')) {
      if (
        !isEmpty(limit?.max_aiv_ltv) ||
        !isEmpty(limit?.max_arv_ltv) ||
        !isEmpty(limit?.max_ltc) ||
        !isEmpty(limit?.max_ltp)
      ) {
        const { maxTotalConstructionFinanced } = loanGlobalSettings
        lists.push({
          label: `Construction Budget + ${
            maxTotalConstructionFinanced - 100
          }% of Construction Budget + Lien Payoff + Closing Cost Estimate + Interest Reserve`,
          value:
            rehabBudget +
            (rehabBudget * (maxTotalConstructionFinanced - 100)) / 100 +
            lienPayoff +
            closingCostEstimate +
            interestReserve,
        })
      }
    }
  }
  if (!isEmpty(limit?.max_ltc) && limit?.ltc) {
    const percent = limit.max_ltc / 100
    lists.push({
      label: `Max LTC: (Min(As Is Appraised Value, Property Purchase Price) + Construction Budget${
        loan.includeInterestReserve ? ' + Interest Reserve' : ''
      }) x ${percent}`,
      value: multiplyPrecisely(
        minPurchasePriceAppraisedValue + rehabBudget + (loan.includeInterestReserve ? interestReserve : 0),
        percent,
      ),
    })
  }
  if (!isEmpty(limit?.max_ltp) && limit?.ltp && propertyPurchasePrice && constructionReserve) {
    const percent = limit.max_ltp / 100
    lists.push({
      label: `Max LTP: Property Purchase Price x ${percent} + Construction Amount Financed`,
      value: multiplyPrecisely(propertyPurchasePrice, percent) + constructionReserve,
    })
  }
  if (!isEmpty(limit?.max_arv_ltv) && limit?.arv_ltv && afterRepairValue) {
    const percent = limit.max_arv_ltv / 100
    lists.push({
      label: `Max ARV-LTV: After Repair Value x ${percent}`,
      value: multiplyPrecisely(afterRepairValue, percent),
    })
  }
  if (!isEmpty(limit?.max_aiv_ltv) && limit?.aiv_ltv) {
    const percent = limit.max_aiv_ltv / 100
    lists.push({
      label: `Max AIV-LTV: Min(As Is Appraised Value, Property Purchase Price) x ${percent} + Construction Amount Financed`,
      value: multiplyPrecisely(minPurchasePriceAppraisedValue, percent) + constructionReserve,
    })
    AIVLTVMIN = multiplyPrecisely(minPurchasePriceAppraisedValue, percent)
  }
  if (!loan.isBPiece && Number.isFinite(maxLoanAmount))
    lists.push({
      label: `Max Loan Amount from RTL Trader`,
      value: maxLoanAmount,
    })
  lists.forEach((item) => {
    if (minValue > item.value) minValue = item.value
  })
  const tooltip = buildMinimumValueTooltip(lists)

  let constructionReserveMinwithoutChangeLoanAmountValidation = -1
  if (AIVLTVMIN !== -1) {
    if (minValue >= proposedLoanAmount)
      constructionReserveMinwithoutChangeLoanAmountValidation = Math.max(0, minValue - AIVLTVMIN)

    if (minValue - AIVLTVMIN >= 0 && minValue - AIVLTVMIN >= proposedLoanAmount - AIVLTVMIN)
      constructionReserveMinwithoutChangeLoanAmountValidation = Math.max(0, proposedLoanAmount - AIVLTVMIN)
  }

  const totalLists = []
  let totalMinValue = MINVALUE
  if (loan.transactionType === 'Refinance') {
    if (visibleFields.includes('rehabBudget')) {
      if (
        !isEmpty(totalLtvLimits?.aivLtv) ||
        !isEmpty(totalLtvLimits?.arvLtv) ||
        !isEmpty(totalLtvLimits?.ltc) ||
        !isEmpty(totalLtvLimits?.ltp)
      ) {
        totalLists.push({
          label:
            'Construction Budget + 20% of Construction Budget + Lien Payoff + Closing Cost Estimate + Interest Reserve',
          value: rehabBudget + rehabBudget * 0.2 + lienPayoff + closingCostEstimate + interestReserve,
        })
      }
    }
  }
  if (totalLtvLimits?.ltc && !isEmpty(totalLtvLimits?.ltc)) {
    const percent = totalLtvLimits.ltc / 100
    totalLists.push({
      label: `Max LTC: (Min(As Is Appraised Value, Property Purchase Price) + Construction Budget${
        loan.includeInterestReserve ? ' + Interest Reserve' : ''
      }) x ${percent}`,
      value: multiplyPrecisely(
        minPurchasePriceAppraisedValue + rehabBudget + (loan.includeInterestReserve ? interestReserve : 0),
        percent,
      ),
    })
  }
  if (totalLtvLimits?.ltp && !isEmpty(totalLtvLimits?.ltp) && propertyPurchasePrice && constructionReserve) {
    const percent = totalLtvLimits.ltp / 100
    totalLists.push({
      label: `Max LTP: Property Purchase Price x ${percent} + Construction Amount Financed`,
      value: multiplyPrecisely(propertyPurchasePrice, percent) + constructionReserve,
    })
  }
  if (totalLtvLimits?.arvLtv && !isEmpty(totalLtvLimits?.arvLtv) && afterRepairValue) {
    const percent = totalLtvLimits.arvLtv / 100
    totalLists.push({
      label: `Max ARV-LTV: After Repair Value x ${percent}`,
      value: multiplyPrecisely(afterRepairValue, percent),
    })
  }
  if (totalLtvLimits?.aivLtv && !isEmpty(totalLtvLimits?.aivLtv)) {
    const percent = totalLtvLimits.aivLtv / 100
    totalLists.push({
      label: `Max AIV-LTV: Min(As Is Appraised Value, Property Purchase Price) x ${percent} + Construction Amount Financed`,
      value: multiplyPrecisely(minPurchasePriceAppraisedValue, percent) + constructionReserve,
    })
  }
  if (loan.isBPiece && Number.isFinite(maxLoanAmount))
    totalLists.push({
      label: `Max Loan Amount from RTL Trader`,
      value: maxLoanAmount,
    })
  totalLists.forEach((item) => {
    if (totalMinValue > item.value) totalMinValue = item.value
  })
  const totalTooltip = buildMinimumValueTooltip(totalLists)

  return {
    tooltip,
    minValue: Math.trunc(minValue),
    lists: lists.length,
    constructionReserveMinwithoutChangeLoanAmountValidation,
    totalTooltip,
    totalMinValue: Math.trunc(totalMinValue),
    totalLists: totalLists.length,
  }
}

export const isNonDSCRProduct = (productType: string, fieldProperties?: Record<string, IVisibleProp>) => {
  let productTypeForRules = productType
  if (!fieldProperties) {
    const state = store.getState()
    const loanFieldProperties = state?.globalConfigReducer?.[GlobalConfigType.LoanFields] as LoanFieldProps
    fieldProperties = (loanFieldProperties?.[FieldOrigin.LoanStructure] || {}) as Record<string, IVisibleProp>
  }
  productTypeForRules = getMappedProductType(productType, fieldProperties)
  return ![ProductTypes.DSCR, ProductTypes.NonQM].includes(productTypeForRules as ProductTypes)
}

export const estimatedClosingAmountBorrower = (
  loan: ILoan,
  loanGlobalSettings: ILoanGlobalSettings,
  isSummary = true,
) => {
  let value = 0,
    valueWithoutConstructionCalc = 0,
    closingTooltip = '',
    closingMaxRehabBudget = 0,
    closingError = '',
    rehabFinancedPercent = '',
    totalRehabFinancedPercent = '',
    totalRehabFinancedTooltip = '',
    label = ''

  label = `Estimated Closing Amount ${loan.transactionType === 'Refinance' ? 'to' : 'from'} Borrower`

  const visibleFields = visibleLoansLogic(loan)

  const { closingCostEstimate, lienPayoff, interestReserve, constructionReserve, rehabBudget, propertyPurchasePrice } =
    analyzeLoanStructureValue(loan, isSummary)
  const proposedLoanAmount = removeComma(loan.proposedLoanAmount)

  if (loan.transactionType === 'Refinance') {
    valueWithoutConstructionCalc =
      removeComma(proposedLoanAmount) -
      removeComma(lienPayoff) -
      removeComma(closingCostEstimate) -
      removeComma(loan.escrowRepairHoldback) -
      removeComma(interestReserve)
    value = valueWithoutConstructionCalc - removeComma(constructionReserve)

    closingTooltip = `Proposed Loan Amount<br/>- Lien Payoff<br/>- Escrow Repair Holdback<br/>- Closing Cost Estimate<br/>- Interest Reserve<br/>- Construction Amount Financed`

    if (!visibleFields.includes('escrowRepairHoldback')) {
      value += removeComma(loan.escrowRepairHoldback)
      closingTooltip = closingTooltip.replace('<br/>- Escrow Repair Holdback', '')
    }
    if (!visibleFields.includes('lienPayoff')) {
      value += removeComma(lienPayoff)
      closingTooltip = closingTooltip.replace('<br/>- Lien Payoff', '')
    }
    if (!visibleFields.includes('constructionReserve')) {
      value += removeComma(constructionReserve)
      closingTooltip = closingTooltip.replace('<br/>- Construction Amount Financed', '')
    }
    if (!visibleFields.includes('interestReserve')) {
      value += removeComma(interestReserve)
      closingTooltip = closingTooltip.replace('<br/>- Interest Reserve', '')
    }

    if (visibleFields.includes('rehabBudget')) {
      closingMaxRehabBudget =
        (removeComma(rehabBudget) * removeComma(loanGlobalSettings.maxPercentOfEstimatedClosingAmountToBorrower)) / 100
      if (value > closingMaxRehabBudget) {
        closingError = `May not exceed ${
          loanGlobalSettings.maxPercentOfEstimatedClosingAmountToBorrower
        }% of Construction Budget which is $${getPrice3decimal(closingMaxRehabBudget)}`
      }
    }
    if (value < 0) label = label.replace('to', 'from')
  } else {
    value =
      removeComma(propertyPurchasePrice) -
      removeComma(proposedLoanAmount) +
      removeComma(loan.escrowRepairHoldback) +
      removeComma(closingCostEstimate) +
      removeComma(interestReserve) +
      removeComma(constructionReserve)
    closingTooltip =
      'Property Purchase Price<br/>- Proposed Loan Amount<br/>+ Escrow Repair Holdback<br/>+ Closing Cost Estimate<br/>+ Interest Reserve<br/>+ Construction Amount Financed'

    if (!visibleFields.includes('escrowRepairHoldback')) {
      value -= removeComma(loan.escrowRepairHoldback)
      closingTooltip = closingTooltip.replace('<br/>+ Escrow Repair Holdback', '')
    }
    if (!visibleFields.includes('constructionReserve')) {
      value -= removeComma(constructionReserve)
      closingTooltip = closingTooltip.replace('<br/>+ Construction Amount Financed', '')
    }
    if (!visibleFields.includes('interestReserve')) {
      value -= removeComma(interestReserve)
      closingTooltip = closingTooltip.replace('<br/>+ Interest Reserve', '')
    }
    if (value < 0) label = label.replace('from', 'to')
  }
  if (visibleFields.includes('rehabBudget') && removeComma(rehabBudget) > 0) {
    rehabFinancedPercent = ((removeComma(constructionReserve) * 100) / removeComma(rehabBudget)).toFixed(2)
    const closingCost = loan.transactionType === 'Refinance' ? (value < 0 ? 0 : value) : 0
    totalRehabFinancedTooltip = `Construction Amount Financed${
      closingCost > 0 ? ' + Estimated Closing Amount to Borrower' : ''
    } / Construction Budget`
    totalRehabFinancedPercent = (
      ((removeComma(closingCost) + removeComma(constructionReserve)) * 100) /
      removeComma(rehabBudget)
    ).toFixed(2)
  }

  return {
    rehabFinancedPercent,
    totalRehabFinancedPercent,
    totalRehabFinancedTooltip,
    value,
    label,
    valueWithoutConstructionCalc,
    closingTooltip,
    closingMaxRehabBudget,
    closingError,
    constructionReserve,
  }
}

export const validateLoanStructureData = (loan: ILoan, rateData: any, errorMessage: boolean = false) => {
  let errors = []
  const { auth } = store.getState()
  if (removeComma(rateData.rate) <= 0 && !auth.profile.isBorrower)
    errors.push(`Interest Rate & Base Price is Required!`)
  const sameEstimate = closingCostEstimateIsSameFeeWorksheet()
  if (!sameEstimate.same) errors.push(sameEstimate.error)
  const sameCredit = isSameStructureApplicationCreditScore()
  if (!sameCredit.same && sameCredit.value !== 0) errors.push(sameCredit.error)
  if (errorMessage && errors.length) toast(errors.join('\n'), { type: 'error' })
  return errors
}

export const isLtvAdjustments = (fields: string[]) => {
  return fields.includes('ltvAdjustment') || fields.includes('aivLtvAdjustment')
}

export const isEmptyPrices = (prices: IPrice[] = []) => {
  let rlt = true

  prices.forEach((item) => {
    if (!!item.Programs.length) rlt = false
  })

  return rlt
}

export const getRateSpreadExitFeeTooltip = (loan: ILoan, data: IPrice_Program_Price) => {
  const rateSpread = removeComma(data?.RateSpread as number)
  const exitFee = removeComma(data?.ExitFee as number)
  const proposedLoanAmount = removeComma(loan.proposedLoanAmount)

  return {
    rateSpread: `$${getPrice2decimal((proposedLoanAmount * rateSpread) / 100 / 12, false, true)}/month`,
    exitFee: `$${getPrice2decimal((proposedLoanAmount * exitFee) / 100, false, true)}`,
  }
}

export const calcInitialLoanAmount = (loan: ILoan, isTotalLoanAmount: boolean = false) => {
  const definedVisibleFields = visibleLoansLogic(loan)
  const { constructionReserve } = analyzeLoanStructureValue(loan)
  const bPieceAmount = removeComma(loan.proposedLoanAmount) - removeComma(loan.aPieceAmount)
  let rlt = removeComma(loan.proposedLoanAmount)
  if (definedVisibleFields.includes('constructionReserve')) rlt -= removeComma(constructionReserve)
  if (holdsOutInterestReserve(loan) && definedVisibleFields.includes('interestReserve'))
    rlt -= removeComma(loan.interestReserve)
  if (definedVisibleFields.includes('isBPiece') && loan.isBPiece && !isTotalLoanAmount) rlt -= removeComma(bPieceAmount)

  if (rlt <= 0) return '0.00'
  return getPrice3decimal(rlt.toFixed(2))
}

export const updateProduct = (products: IProduct[], prices: IPrice[], lockDays: ILockDay[], newData: IProduct) => {
  const newProducts = cloneDeep(products)
  const newPrices = cloneDeep(prices)
  const newLockDays = cloneDeep(lockDays)

  const state = store.getState()
  const loan = state.loan as ILoan

  newProducts.forEach((item) => {
    if (item.Id === newData.Id) {
      item.Description = newData.Description
    }
  })
  newPrices.forEach((item) => {
    if (item.ProductID === newData.Id) {
      item.ProductName = newData.Description

      if (!isNonDSCRProduct(loan.productType))
        item.Programs.forEach((prog) => {
          prog.Prices.forEach((price) => {
            const pitis = combinedMonthlyExpense(loan, {
              rate: price.Rate,
              type: item.ProductName,
            })
            price.Dscr = pitis.dscr
          })
        })
    }
  })

  return { newProducts, newPrices, newLockDays }
}

export const addNewProduct = (
  products: IProduct[],
  prices: IPrice[],
  programs: IProgram[],
  lockDays: ILockDay[],
  data: IManualPricingSetting,
) => {
  const newProducts = cloneDeep(products)
  const newPrices = cloneDeep(prices)
  const newPrograms = cloneDeep(programs)
  const newLockDays = cloneDeep(lockDays)

  const { loan, loanDetail } = store.getState()
  const globalSetting = loanDetail.loanGlobalSettings as ILoanGlobalSettings

  const isExist = newProducts.find((item) => item.Description === data.productName) as IProduct
  if (isExist) {
    toast(`The same product already exists`, { type: 'error' })
    return { newProducts, newPrices, newPrograms, newLockDays }
  }

  const newProduct: IProduct = {
    Id: 0,
    Description: data.productName,
    IsInterestOnly: data?.interestOnly,
  }
  if (!!newProducts.length) {
    const maxIdProduct = newProducts.reduce((maxProduct, currentProduct) => {
      return currentProduct.Id > maxProduct.Id ? currentProduct : maxProduct
    })
    newProduct.Id = maxIdProduct.Id + 1
  } else newProduct.Id = 0
  newProducts.push(newProduct)

  data.programs.forEach((cofig, index) => {
    const pitis = !isNonDSCRProduct(loan.productType)
      ? combinedMonthlyExpense(loan, {
          rate: cofig.rate,
          type: data.productName,
        })
      : undefined

    const newProgram: IProgram = {
      ID: 0,
      Name: cofig.programName,
      PriceOption: globalSetting.priceDisplay || 'Price',
      ManualID: cofig.id,
    }

    const programExist = newPrograms.find((item) => item.Name === newProgram.Name)
    if (programExist) newProgram.ID = programExist.ID
    else {
      if (!!newPrograms.length) {
        const maxIdProgram = newPrograms.reduce((maxProgram, currentProgram) => {
          return currentProgram.ID > maxProgram.ID ? currentProgram : maxProgram
        })
        newProgram.ID = maxIdProgram.ID + 1
      } else newProgram.ID = 0
      newPrograms.push(newProgram)
    }

    const lockTermPrices: ILockTermPrice[] = []
    if (!newLockDays.length)
      newLockDays.push({
        description: String(data.lockTerm),
      })

    newLockDays.forEach((lock) => {
      lockTermPrices.push({
        LockDay: lock.description,
        BasePrice: cofig.price,
        LockTermLLPA: 0,
        Price: String(cofig.price),
      })
    })

    if (index == 0)
      newPrices.push({
        ProductID: newProduct.Id,
        ProductName: newProduct.Description,
        Programs: [
          {
            ProgramID: newProgram.ID,
            ProgramManualID: newProgram.ManualID,
            ProgramName: newProgram.Name,
            Margin: NaN,
            TierLevel: NaN,
            Prices: [
              {
                Rate: cofig.rate,
                BaseRate: cofig.rate,
                Dscr: pitis?.dscr,
                RateSpread: cofig.rateSpread,
                ExitFee: cofig.exitFee,
                LockTermPrices: lockTermPrices,
                CalcHistory: [],
                LLPA: {},
                Limit: {},
              },
            ],
            LLPA: {},
            CalcHistory: [],
            LTV: { from: NaN, to: NaN },
          },
        ],
      })
    else {
      const targetIndex = newPrices.length - 1
      newPrices[targetIndex].Programs.push({
        ProgramID: newProgram.ID,
        ProgramName: newProgram.Name,
        ProgramManualID: newProgram.ManualID,
        Margin: NaN,
        TierLevel: NaN,
        Prices: [
          {
            Rate: cofig.rate,
            BaseRate: cofig.rate,
            Dscr: pitis?.dscr,
            RateSpread: cofig.rateSpread,
            ExitFee: cofig.exitFee,
            LockTermPrices: lockTermPrices,
            CalcHistory: [],
            LLPA: {},
            Limit: {},
          },
        ],
        LLPA: {},
        CalcHistory: [],
        LTV: { from: NaN, to: NaN },
      })
    }
  })

  return { newProducts, newPrices, newPrograms, newLockDays }
}

export const deleteProduct = (products: IProduct[], programs: IProgram[], prices: IPrice[], product: IProduct) => {
  const newProducts = cloneDeep(products)
  const newPrices = cloneDeep(prices)
  const nPrograms = cloneDeep(programs)

  let targetIndex = newProducts.findIndex((item) => item.Id === product.Id)
  newProducts.splice(targetIndex, 1)

  targetIndex = newPrices.findIndex((item) => item.ProductID === product.Id)
  newPrices.splice(targetIndex, 1)

  const requiredIDs: number[] = []
  newPrices.forEach((item) => {
    item.Programs.forEach((v) => {
      requiredIDs.push(v.ProgramID)
    })
  })
  const newPrograms = nPrograms.filter((item) => requiredIDs.includes(item.ID))

  return { newProducts, newPrices, newPrograms }
}

export const addNewProgram = (
  programs: IProgram[],
  prices: IPrice[],
  lockDays: ILockDay[],
  product: IProduct,
  data: IManualProgram[],
) => {
  const newPrograms = cloneDeep(programs)
  const newPrices = cloneDeep(prices)

  const { loan, loanDetail } = store.getState()
  const globalSetting = loanDetail.loanGlobalSettings as ILoanGlobalSettings

  data.forEach((value) => {
    const newProgram: IProgram = {
      ID: 0,
      Name: value.programName,
      PriceOption: globalSetting.priceDisplay || 'Price',
      ManualID: value.id,
    }
    const isExist = newPrograms.find((item) => item.Name === newProgram.Name)
    if (isExist) newProgram.ID = isExist.ID
    else {
      if (!!newPrograms.length) {
        const maxIdProgram = newPrograms.reduce((maxProgram, currentProgram) => {
          return currentProgram.ID > maxProgram.ID ? currentProgram : maxProgram
        })
        newProgram.ID = maxIdProgram.ID + 1
      } else newProgram.ID = 0
      newPrograms.push(newProgram)
    }

    const targetProgramConfig =
      globalSetting.manualPricingSetting.programs.find((item) => item.id === newProgram.ManualID) ??
      globalSetting.manualPricingSetting.programs[0]

    newPrices.forEach((item) => {
      if (item.ProductID === product.Id) {
        const alreadyExist = item.Programs.find((data) => data.ProgramName === newProgram.Name)
        if (alreadyExist) return { newPrograms, newPrices }

        const pitis = !isNonDSCRProduct(loan.productType)
          ? combinedMonthlyExpense(loan, {
              rate: targetProgramConfig.rate,
              type: item.ProductName,
            })
          : undefined

        const lockTermPrices: ILockTermPrice[] = []

        lockDays.forEach((lock) => {
          lockTermPrices.push({
            LockDay: lock.description,
            BasePrice: targetProgramConfig.price,
            LockTermLLPA: 0,
            Price: String(targetProgramConfig.price),
          })
        })
        item.Programs.push({
          ProgramID: newProgram.ID,
          ProgramName: newProgram.Name,
          Margin: NaN,
          TierLevel: NaN,
          Prices: [
            {
              Rate: targetProgramConfig.rate,
              BaseRate: targetProgramConfig.rate,
              Dscr: pitis?.dscr,
              RateSpread: targetProgramConfig.rateSpread,
              ExitFee: targetProgramConfig.exitFee,
              LockTermPrices: lockTermPrices,
              CalcHistory: [],
              LLPA: {},
              Limit: {},
            },
          ],
          LLPA: {},
          CalcHistory: [],
          LTV: { from: NaN, to: NaN },
        })
      }
    })
  })

  return { newPrograms, newPrices }
}

export const updateProgram = (programs: IProgram[], prices: IPrice[], program: IProgram) => {
  const newPrograms = cloneDeep(programs)
  const newPrices = cloneDeep(prices)

  newPrograms.forEach((item) => {
    if (item.ID === program.ID) {
      item.Name = program.Name
    }
  })

  newPrices.forEach((item) => {
    item.Programs.forEach((data) => {
      if (data.ProgramID === program.ID) {
        data.ProgramName = program.Name
      }
    })
  })

  return { newPrograms, newPrices }
}

export const deleteProgram = (programs: IProgram[], prices: IPrice[], productID: number, programID: number) => {
  const newPrograms = cloneDeep(programs)
  const newPrices = cloneDeep(prices)

  newPrices.find((item) => {
    if (item.ProductID === productID) {
      const targetIndex = item.Programs.findIndex((prog) => prog.ProgramID === programID)
      item.Programs.splice(targetIndex, 1)
    }
  })

  const removeIndex = programs.findIndex((item) => item.ID === programID)
  newPrograms.splice(removeIndex, 1)

  return { newPrograms, newPrices }
}

export const updateLockDay = (
  program: IProgram,
  prices: IPrice[],
  lockDays: ILockDay[],
  lockDayIndex: number,
  value: number,
  isUpdate: boolean,
) => {
  const newPrices = cloneDeep(prices)
  const newLockDays = cloneDeep(lockDays)

  if (isUpdate) {
    newLockDays[lockDayIndex].description = String(value)

    newPrices.forEach((item) => {
      item.Programs.forEach((data) => {
        data.Prices.forEach((rate) => {
          if (rate.LockTermPrices[lockDayIndex]) rate.LockTermPrices[lockDayIndex].LockDay = String(value)
        })
      })
    })
  } else {
    const { loanDetail } = store.getState()
    const globalSetting = loanDetail.loanGlobalSettings as ILoanGlobalSettings
    const targetProgramConfig =
      globalSetting.manualPricingSetting.programs.find((item) => item.id === program.ManualID) ??
      globalSetting.manualPricingSetting.programs[0]

    value = globalSetting.manualPricingSetting.lockTerm
    newLockDays.push({
      description: String(value),
    })

    newPrices.forEach((item) => {
      item.Programs.forEach((prog) => {
        prog.Prices.forEach((price) => {
          price.LockTermPrices.push({
            LockDay: String(value),
            BasePrice: targetProgramConfig.price,
            LockTermLLPA: 0,
            Price: String(targetProgramConfig.price),
          })
        })
      })
    })
  }

  return { newPrices, newLockDays }
}

export const deleteLockDay = (prices: IPrice[], lockDays: ILockDay[], index: number) => {
  const newPrices = cloneDeep(prices)
  const newLockDays = cloneDeep(lockDays)

  newLockDays.splice(index, 1)
  newPrices.forEach((item) => {
    item.Programs.forEach((prog) => {
      prog.Prices.forEach((price) => {
        price.LockTermPrices.splice(index, 1)
      })
    })
  })

  return { newLockDays, newPrices }
}

export const addRate = (
  program: IProgram,
  prices: IPrice[],
  lockDays: ILockDay[],
  selectedProductId: number,
  selectedProgramId: number,
  index: number,
  value: number,
) => {
  const newPrices = cloneDeep(prices)

  const { loanDetail, loan } = store.getState()
  const globalSetting = loanDetail.loanGlobalSettings as ILoanGlobalSettings
  const targetProgramConfig =
    globalSetting.manualPricingSetting.programs.find((item) => item.id === program.ManualID) ??
    globalSetting.manualPricingSetting.programs[0]

  newPrices.forEach((item) => {
    if (item.ProductID === selectedProductId) {
      item.Programs.forEach((prog) => {
        if (prog.ProgramID === selectedProgramId) {
          if (index === -1) {
            const lockTermPrices: ILockTermPrice[] = []
            lockDays.forEach((lock) => {
              lockTermPrices.push({
                LockDay: lock.description,
                BasePrice: targetProgramConfig.price,
                LockTermLLPA: 0,
                Price: String(targetProgramConfig.price),
              })
            })

            const pitis = !isNonDSCRProduct(loan.productType)
              ? combinedMonthlyExpense(loan, {
                  rate: targetProgramConfig.rate,
                  type: item.ProductName,
                })
              : undefined

            prog.Prices.push({
              Rate: targetProgramConfig.rate,
              BaseRate: targetProgramConfig.rate,
              Dscr: pitis?.dscr,
              RateSpread: targetProgramConfig.rateSpread,
              ExitFee: targetProgramConfig.exitFee,
              LockTermPrices: lockTermPrices,
              CalcHistory: [],
              LLPA: {},
              Limit: {},
            })
          } else {
            prog.Prices[index].Rate = value

            if (!isNonDSCRProduct(loan.productType)) {
              const pitis = combinedMonthlyExpense(loan, {
                rate: value,
                type: item.ProductName,
              })
              prog.Prices[index].Dscr = pitis?.dscr
            }
          }
        }
      })
    }
  })

  return { newPrices }
}

export const deleteRate = (prices: IPrice[], selectedProductId: number, selectedProgramId: number, index: number) => {
  const newPrices = cloneDeep(prices)

  newPrices.forEach((item) => {
    if (item.ProductID === selectedProductId) {
      item.Programs.forEach((prog) => {
        if (prog.ProgramID === selectedProgramId) {
          prog.Prices.splice(index, 1)
        }
      })
    }
  })

  return { newPrices }
}

export const updatePrice = (
  prices: IPrice[],
  selectedProductId: number,
  selectedProgramId: number,
  rateIndex: number,
  lockDayIndex: number,
  price: number,
) => {
  const newPrices = cloneDeep(prices)

  newPrices.forEach((item) => {
    if (item.ProductID === selectedProductId) {
      item.Programs.forEach((prog) => {
        if (prog.ProgramID === selectedProgramId) {
          prog.Prices[rateIndex].LockTermPrices[lockDayIndex].BasePrice = price
          prog.Prices[rateIndex].LockTermPrices[lockDayIndex].Price = price.toString()
        }
      })
    }
  })

  return { newPrices }
}

export const updateRateSpreadExitFee = (
  prices: IPrice[],
  selectedProductId: number,
  selectedProgramId: number,
  index: number,
  key: 'RateSpread' | 'ExitFee',
  value: number,
) => {
  const newPrices = cloneDeep(prices)

  newPrices.forEach((item) => {
    if (item.ProductID === selectedProductId) {
      item.Programs.forEach((prog) => {
        if (prog.ProgramID === selectedProgramId) {
          prog.Prices[index][key] = value
        }
      })
    }
  })

  return { newPrices }
}

export const getUWFee = (
  loan: ILoan,
  underwritingFees: IUnderwritingFee[],
  globalSetting: ILoanGlobalSettings,
  loanFieldProps: Record<string, IVisibleProp>,
) => {
  const { productType, transactionType, propertyType, proposedLoanAmount, underwritingFee } = loan
  const { visible, defaultValue } = loanFieldProps.underwritingFee
  if (visible == true) {
    return removeComma(underwritingFee)
  } else {
    const targetUWfee = underwritingFees.find(
      (item) =>
        item.productType === productType &&
        item.transactionType === transactionType &&
        item.propertyType === propertyType &&
        removeComma(item.loanAmountFrom) <= removeComma(proposedLoanAmount) &&
        removeComma(item.loanAmountTo) >= removeComma(proposedLoanAmount),
    )

    if (targetUWfee && removeComma(targetUWfee.underwritingFee) !== 0) return removeComma(targetUWfee.underwritingFee)

    if (defaultValue && removeComma(defaultValue) !== 0) {
      return removeComma(defaultValue)
    }

    return removeComma(globalSetting.defaultUnderwritingFee)
  }
}

export const validateMinMaxLimitPerTypes = (
  amount: number,
  limit: IMinMaxLimitPerTypes,
  loanAmount: number,
  title: string,
) => {
  const { minAmount, maxAmount, minPercent, maxPercent } = limit

  if (!isEmpty(minAmount) && !isEmpty(maxAmount)) {
    if (amount < minAmount || amount > maxAmount)
      return `${title} should be between $${getPrice3decimal(minAmount)} and $${getPrice3decimal(maxAmount)}`
  } else if (!isEmpty(minAmount) && isEmpty(maxAmount)) {
    if (amount < minAmount) return `${title} should be greater than $${getPrice3decimal(minAmount)}`
  } else if (isEmpty(minAmount) && !isEmpty(maxAmount)) {
    if (amount > maxAmount) return `${title} should be less than $${getPrice3decimal(maxAmount)}`
  }

  if (!isEmpty(minPercent) && !isEmpty(maxPercent)) {
    const minLimit = (loanAmount * minPercent) / 100
    const maxLimit = (loanAmount * maxPercent) / 100
    if (amount < minLimit || amount > maxLimit)
      return `${title} should be between ${getPrice3decimal(minPercent)}% and ${getPrice3decimal(
        maxPercent,
      )}% of Loan Amount`
  } else if (!isEmpty(minPercent) && isEmpty(maxPercent)) {
    const minLimit = (loanAmount * minPercent) / 100
    if (amount < minLimit) return `${title} should be greater than ${getPrice3decimal(minPercent)}% of Loan Amount`
  } else if (isEmpty(minPercent) && !isEmpty(maxPercent)) {
    const maxLimit = (loanAmount * maxPercent) / 100
    if (amount > maxLimit) return `${title} should be less than ${getPrice3decimal(maxPercent)}% of Loan Amount`
  }

  return ''
}

export const convertLoanDetailStatus = (value: IRange) => {
  if (value?.from === undefined) return ''
  if (value.from === null && value.to !== null) return `Max = ${getPrice3decimal(value.to)}`
  else if (value.from !== null && value.to === null) return `Min = ${getPrice3decimal(value.from)}`
  else if (value.from !== null && value.to !== null)
    return `Min = ${getPrice3decimal(value.from)}, Max = ${getPrice3decimal(value.to)}`
  else return ''
}

export const getVacantUnitsError = (vacant: any, vacantIsPercentage: boolean, numberOfUnits: any) => {
  if (isEmpty(vacant) && vacant !== 0) return ''
  const vacantValue = removeComma(vacant)
  if (vacantIsPercentage) {
    return ''
  }
  if (isEmpty(numberOfUnits) && numberOfUnits !== 0) return ''
  const units = removeComma(numberOfUnits)
  if (vacantValue > units) return 'Vacant Units cannot exceed Number of Units'
  return ''
}

export const getLoanDetailsValue = (key: string, loan: ILoan, fieldProperties: Record<string, IVisibleProp>) => {
  if (key === 'estimatedCreditScore' && loan.isNoScore) return 'No Score'
  if (key === 'vacant') {
    const value = loan.vacant
    if (isEmpty(value) && value !== 0) return ''
    return loan.vacantIsPercentage ? `${value}%` : String(value)
  }
  return convertValueToLabel(key, (loan as any)[key], fieldProperties)
}

export const getRangeFromString = (value: string) => {
  if (!value) return { from: null, to: null }
  const valueArr = value.split('-')
  if (valueArr.length !== 2) return { from: null, to: null }
  const rlt = {
    from: valueArr[0] == 'null' ? null : removeComma(valueArr[0]),
    to: valueArr[1] == 'null' ? null : removeComma(valueArr[1]),
  }
  return rlt
}

export const getLoanDetailChanges = (
  originLoanDetail: ILoan | undefined,
  loan: ILoan,
  loanFields: Record<string, InputType>,
  loanProcessData: ILoanProcess,
  changedLoanProcessData: ILoanProcess,
  multiPropertiesFields: Record<string, InputType>[],
  visibleFields: string[],
  propertyVisibleFields: string[][],
) => {
  let rlt: Record<string, ILoanDetailChangedJson> = {}
  if (!originLoanDetail) return rlt
  let ltvChanged = false
  let cltvChanged = false

  Object.keys(loanFields).map((key) => {
    if (!isCustomFieldKey(key) && (originLoanDetail as any)[key] === undefined) return
    if (isCustomFieldKey(key) && ['ActionButton'].includes(loanFields[key].inputType)) return
    if (!visibleFields.includes(key)) return
    let newValue = (loan as any)[key]
    let originValue = (originLoanDetail as any)[key]
    if (isCustomFieldKey(key) && ['FileTable'].includes(loanFields[key].inputType)) {
      newValue = (loan as any)[key]?.[0]?.name ?? (loan as any)[key] ?? ''
      originValue = (originLoanDetail as any)[key]?.[0]?.name ?? (originLoanDetail as any)[key] ?? ''
    } else if (loanFields[key].type === 'thousandSep' || loanFields[key].type === 'thousandSepNoDecimal') {
      newValue = isEmpty(newValue) ? null : removeComma(newValue)
      originValue = isEmpty(originValue) ? null : removeComma(originValue)
    } else if (
      loanFields[key].inputType === 'text' &&
      (loanFields[key].type === 'text' || isEmpty(loanFields[key].type))
    ) {
      newValue = isEmpty(newValue) ? null : String(newValue).trim()
      originValue = isEmpty(originValue) ? null : String(originValue).trim()
    }
    if (newValue != originValue) {
      if (!['ltv', 'cltv'].includes(key))
        rlt[key] = {
          key,
          label: loanFields[key].title,
          origin: originValue,
          changed: newValue,
        }

      if (key === 'additionalPropertyAddress') {
        try {
          if (JSON.stringify(loan[key]) === JSON.stringify((originLoanDetail as any)[key])) delete rlt[key]
          if (loan[key]?.length === 0 && (originLoanDetail as any)[key]?.length === 0) {
            delete rlt[key]
          }
        } catch {}
      }
      if (calcLTVFields.includes(key)) ltvChanged = true
      if (calcCLTVFields.includes(key)) cltvChanged = true
      if (key === 'subjectPropertyAddress') {
        const key2 = 'propertyCounty'
        if (loan[key2] !== (originLoanDetail as any)[key2]) {
          rlt[key2] = {
            key: 'propertyCounty',
            label: 'Property County',
            origin: (originLoanDetail as any)[key2],
            changed: loan[key2],
          }
        }
      }
      if (dscrRelatedFields.includes(key)) {
        const originDSCR = combinedMonthlyExpense(originLoanDetail, loanProcessData).dscr
        const changedDSCR = combinedMonthlyExpense(loan, changedLoanProcessData).dscr

        if (Number(originDSCR) !== Number(changedDSCR))
          rlt['dscr'] = {
            key: 'dscr',
            label: 'Calculated DSCR',
            origin: originDSCR,
            changed: changedDSCR,
          }
      }
      // Track B Piece changes when aPieceAmount or proposedLoanAmount changes
      if (
        (key === 'aPieceAmount' || key === 'proposedLoanAmount') &&
        visibleFields.includes('isBPiece') &&
        loan.isBPiece
      ) {
        const originBPiece = originLoanDetail.isBPiece
          ? removeComma(originLoanDetail.proposedLoanAmount) - removeComma(originLoanDetail.aPieceAmount)
          : ''
        const changedBPiece = removeComma(loan.proposedLoanAmount) - removeComma(loan.aPieceAmount)

        if (Number(originBPiece) !== Number(changedBPiece)) {
          rlt['bPiece'] = {
            key: 'bPiece',
            label: 'B Piece',
            origin: originBPiece !== '' ? thousandSeperator(originBPiece) : '',
            changed: thousandSeperator(changedBPiece),
          }
        }
      }
    }
  })
  if (ltvChanged) {
    const _origin = calculateLTV(originLoanDetail)
    const _changed = calculateLTV(loan)
    if (Number(_origin) !== Number(_changed)) {
      rlt['ltv'] = {
        key: 'LTV',
        label: 'LTV',
        origin: convertLTV(_origin),
        changed: convertLTV(_changed),
      }
    }
  }
  if (cltvChanged) {
    const _origin = calculateLTV(originLoanDetail, true)
    const _changed = calculateLTV(loan, true)
    if (Number(_origin) !== Number(_changed)) {
      rlt['cltv'] = {
        key: 'CLTV',
        label: 'CLTV',
        origin: convertLTV(_origin),
        changed: convertLTV(_changed),
      }
    }
  }
  ltvChanged = false
  cltvChanged = false
  if (!originLoanDetail?.multipleProperties || multiPropertiesFields?.length !== loan?.multipleProperties?.length)
    return rlt
  multiPropertiesFields.forEach((item, index) => {
    Object.keys(item).map((key) => {
      if (!propertyVisibleFields[index]?.includes(key) || !MultiPropertyFields.includes(key)) return
      let newValue = (loan.multipleProperties?.[index] as any)?.[key] ?? ''
      let originValue = (originLoanDetail.multipleProperties?.[index] as any)?.[key] ?? ''
      if (item[key].type === 'thousandSep' || item[key].type === 'thousandSepNoDecimal') {
        newValue = removeComma(newValue)
        originValue = isEmpty(originValue) ? null : removeComma(originValue)
      } else if (item[key].inputType === 'text' && (item[key].type === 'text' || isEmpty(item[key].type))) {
        newValue = String(newValue).trim()
        originValue = isEmpty(originValue) ? null : String(originValue).trim()
      }
      if (newValue != originValue) {
        if (calcLTVFields.includes(key)) ltvChanged = true
        if (calcCLTVFields.includes(key)) cltvChanged = true
      }
    })
  })
  if (ltvChanged) {
    const _origin = calculateLTV(originLoanDetail)
    const _changed = calculateLTV(loan)
    if (Number(_origin) !== Number(_changed)) {
      rlt['ltv'] = {
        key: 'LTV',
        label: 'LTV',
        origin: convertLTV(_origin),
        changed: convertLTV(_changed),
      }
    }
  }
  if (cltvChanged) {
    const _origin = calculateLTV(originLoanDetail, true)
    const _changed = calculateLTV(loan, true)
    if (Number(_origin) !== Number(_changed)) {
      rlt['cltv'] = {
        key: 'CLTV',
        label: 'CLTV',
        origin: convertLTV(_origin),
        changed: convertLTV(_changed),
      }
    }
  }
  return rlt
}

export const getPropertyDetailChanges = (
  originLoanDetail: ILoan | undefined,
  loan: ILoan,
  multiPropertiesFields: Record<string, InputType>[],
  propertyVisibleFields: string[][],
  loanProcessData: ILoanProcess,
  changedLoanProcessData: ILoanProcess,
  isMultipleProperties: boolean,
) => {
  let rlt: IPropertyChangedJson[] = []
  if (!isMultipleProperties) return rlt
  if (!originLoanDetail?.multipleProperties || multiPropertiesFields?.length !== loan?.multipleProperties?.length)
    return rlt
  multiPropertiesFields.forEach((item, index) => {
    Object.keys(item).map((key) => {
      if (!propertyVisibleFields[index]?.includes(key) || !MultiPropertyFields.includes(key)) return
      let newValue = (loan.multipleProperties?.[index] as any)?.[key] ?? ''
      let originValue = (originLoanDetail.multipleProperties?.[index] as any)?.[key] ?? ''
      if (item[key].type === 'thousandSep' || item[key].type === 'thousandSepNoDecimal') {
        newValue = isEmpty(newValue) ? null : removeComma(newValue)
        originValue = isEmpty(originValue) ? null : removeComma(originValue)
      } else if (item[key].inputType === 'text' && (item[key].type === 'text' || isEmpty(item[key].type))) {
        newValue = isEmpty(newValue) ? null : String(newValue).trim()
        originValue = isEmpty(originValue) ? null : String(originValue).trim()
      }
      if (newValue != originValue) {
        rlt.push({
          id: loan.multipleProperties[index].id,
          index,
          key,
          label: item[key].title,
          origin: originValue,
          changed: newValue,
        })

        if (dscrRelatedFields.includes(key)) {
          const originDSCR = !!originLoanDetail?.multipleProperties?.[index]
            ? combinedPropertyMonthlyExpense(
                originLoanDetail,
                originLoanDetail.multipleProperties[index],
                loanProcessData,
              ).dscr
            : ''
          const changedDSCR = combinedPropertyMonthlyExpense(
            loan,
            loan.multipleProperties[index],
            changedLoanProcessData,
          ).dscr
          if (Number(originDSCR) !== Number(changedDSCR)) {
            const dscrIndex = rlt.findIndex(
              (item) => item.id === loan.multipleProperties[index].id && item.key === 'dscr',
            )
            if (dscrIndex === -1)
              rlt.push({
                id: loan.multipleProperties[index].id,
                index,
                key: 'dscr',
                label: 'Calculated DSCR',
                origin: originDSCR,
                changed: changedDSCR,
              })
            else
              rlt[dscrIndex] = {
                id: loan.multipleProperties[index].id,
                index,
                key: 'dscr',
                label: 'Calculated DSCR',
                origin: originDSCR,
                changed: changedDSCR,
              }
          }
        }
      }
    })
  })
  return rlt
}

export const changePoolLoanField = (
  key: string,
  value: any,
  importedLoans: Record<string, any>[],
  selectedLoan: string,
  loanFields: Record<string, InputType>,
  loanDetail: ILoanDetail,
) => {
  const newLoans = cloneDeep(importedLoans)
  const index = importedLoans.findIndex((item) => item['Loan #'] == selectedLoan)
  if (index === -1 || !loanFields[key]) return newLoans

  // Pool rows are spreadsheet-shaped and use display titles as keys. Keep every value by its
  // stable field key too; a custom field can have the same title as a real loan column.
  newLoans[index].__loanFieldValues = {
    ...(newLoans[index].__loanFieldValues || {}),
    [key]: value,
  }
  // Do not overwrite the spreadsheet column for custom fields: a colliding custom "Loan Purpose"
  // title would otherwise replace the enum-backed Loan Purpose value on the next row load.
  if (!isCustomFieldKey(key)) newLoans[index][loanFields[key].title] = value

  if (key === 'transactionType' && value === 'Refinance')
    newLoans[index][loanFields['propertyPurchasePrice'].title] = null
  if (key === 'experience') {
    const { from, to } = getRangeFromString(value)
    if (from === to && from !== null) newLoans[index][loanFields['howManyExperiences'].title] = from
  }
  if (key === 'rehabBudget') {
    const { minTotalConstructionFinanced } = loanDetail.loanGlobalSettings
    if (minTotalConstructionFinanced === 100)
      newLoans[index][loanFields['constructionReserve'].title] = removeComma(value)
  }
  if (key === 'productType' && isNonDSCRProduct(value))
    newLoans[index][loanFields['amortizationType'].title] = 'IO - Fixed'
  if (key === 'propertyType' && ['SFR-Detached', 'SFR-Attached', 'Industrial'].includes(value))
    newLoans[index][loanFields['numberOfUnits'].title] = 1

  return newLoans
}

export const getCustomRange = (value: string) => {
  if (value.toUpperCase() === 'N') return `null-null`
  if (value.includes('+')) return `${removeComma(value)}-null`
  if (value.includes('<=')) return `null-${removeComma(value)}`
  if (value.split('-').length === 1) return `${removeComma(value)}-${removeComma(value)}`
  else return value
}

export const getBankruptcyRange = (value: string) => {
  if (value.toUpperCase() === 'N') return `null-null-null`
  if (value.toLowerCase() === 'active') return `0-null-null`
  if (value.includes('+')) {
    const arr = value.split('-')
    return `${arr[0]}-${removeComma(arr[1])}-null`
  }
  if (value.includes('<=')) {
    const arr = value.split('-')
    return `${arr[0]}-null-${removeComma(arr[1])}`
  }
  const arr = value.split('-')
  return `${arr[0]}-${arr[1]}-${arr[2]}`
}

export const canLockRateByLoanStatus = (
  loanDetail: ILoanDetail,
  lockRateRestrictions: any,
  key: 'initialLock' | 'relock' | 'lockExtension',
) => {
  const { loanStatus } = loanDetail
  return lockRateRestrictions?.[key]?.loanStatus?.[loanStatus] !== false
}

export const validateLoanDetailForPricing = (
  loan: ILoan,
  loanFields: Record<string, InputType>,
  multiPropertiesFields: Record<string, InputType>[],
  loanDetail: ILoanDetail,
  priceAdjustmentObject: Record<string, IPriceAdjustment>,
  visibleFields: string[],
  propertyVisibleFields: string[][],
  isMultipleProperties: boolean,
) => {
  const {
    minTotalConstructionFinanced,
    maxTotalConstructionFinanced,
    minConstructionFinancedHoldback,
    maxConstructionFinancedHoldback,
  } = loanDetail.loanGlobalSettings
  let flag = true
  const errorFields: string[] = []
  visibleFields.map((key) => {
    if (priceLoanRelatedFields.includes(key)) {
      if (loanFields[key]?.error?.length) {
        if (
          validatePriceAdjustment(priceAdjustmentObject[key], key, (loan as any)[key]) &&
          !isEmpty((loan as any)[key])
        ) {
        } else {
          flag = false
          errorFields.push(key)
        }
      }
    }
  })
  if (isMultipleProperties)
    propertyVisibleFields.forEach((item, index) => {
      item.forEach((key: string) => {
        if (MultiPropertyFields.includes(key) && key !== 'estimatedDscr')
          if (priceLoanRelatedFields.includes(key)) {
            if (multiPropertiesFields[index]?.[key]?.error?.length) {
              if (
                validatePriceAdjustment(
                  priceAdjustmentObject[key],
                  key,
                  (loan.multipleProperties[index] as any)[key],
                ) &&
                !isEmpty((loan.multipleProperties[index] as any)[key])
              ) {
              } else {
                flag = false
                errorFields.push(`additionalProperty_${index}_${key}`)
              }
            }
          }
      })
    })
  const { value, closingError, rehabFinancedPercent, totalRehabFinancedPercent } = estimatedClosingAmountBorrower(
    loan,
    loanDetail.loanGlobalSettings,
  )
  if (
    !validatePriceAdjustment(
      priceAdjustmentObject['atClosingBorrowerAmount'],
      'atClosingBorrowerAmount',
      Math.abs(value),
    ) &&
    closingError.length
  ) {
    flag = false
    errorFields.push('atClosingBorrowerAmount')
  }
  const hasPriceAdjustmentForConstructionFinancedHoldback = validatePriceAdjustment(
    priceAdjustmentObject['constructionFinancedHoldback'],
    'constructionFinancedHoldback',
    rehabFinancedPercent,
  )
  if (
    !hasPriceAdjustmentForConstructionFinancedHoldback &&
    removeComma(rehabFinancedPercent) > removeComma(maxConstructionFinancedHoldback)
  ) {
    flag = false
    errorFields.push('constructionFinancedHoldback')
  }
  if (
    !hasPriceAdjustmentForConstructionFinancedHoldback &&
    removeComma(rehabFinancedPercent) > 0 &&
    removeComma(rehabFinancedPercent) < removeComma(minConstructionFinancedHoldback)
  ) {
    errorFields.push('constructionFinancedHoldback')
    flag = false
  }
  const hasPriceAdjustmentForTotalRehabFinancedPercent = validatePriceAdjustment(
    priceAdjustmentObject['totalRehabFinancedPercent'],
    'totalRehabFinancedPercent',
    totalRehabFinancedPercent,
  )
  if (
    !hasPriceAdjustmentForTotalRehabFinancedPercent &&
    removeComma(totalRehabFinancedPercent) > removeComma(maxTotalConstructionFinanced)
  ) {
    errorFields.push('totalRehabFinancedPercent')
    flag = false
  }
  if (
    !hasPriceAdjustmentForTotalRehabFinancedPercent &&
    removeComma(totalRehabFinancedPercent) > 0 &&
    removeComma(totalRehabFinancedPercent) < removeComma(minTotalConstructionFinanced)
  ) {
    errorFields.push('totalRehabFinancedPercent')
    flag = false
  }
  if (isMultipleProperties && loan.howManyProperties != (loan?.multipleProperties?.length || 0) + 1) {
    errorFields.push('howManyProperties')
    flag = false
  }
  console.log('errorFields', errorFields)
  return flag
}

export const validateLoanDetailForSaveToPipeline = (
  loan: ILoan,
  loanFields: Record<string, InputType>,
  multiPropertiesFields: Record<string, InputType>[],
  visibleFields: string[],
  propertyVisibleFields: string[][],
  isMultipleProperties: boolean,
) => {
  let flag = true
  const errorFields: string[] = []
  visibleFields.map((key) => {
    if (!!loanFields[key]?.error?.length && loanFields[key].error === REQUIRED_FIELD_ERROR_MESSAGE) {
      errorFields.push(key)
      flag = false
    }
  })
  if (isMultipleProperties) {
    if (loan.howManyProperties != (loan?.multipleProperties?.length || 0) + 1) {
      errorFields.push('howManyProperties')
      flag = false
    }
    propertyVisibleFields.forEach((item, index) => {
      item.forEach((key) => {
        if (MultiPropertyFields.includes(key) && key !== 'estimatedDscr')
          if (
            !!multiPropertiesFields[index][key]?.error?.length &&
            multiPropertiesFields[index][key].error === REQUIRED_FIELD_ERROR_MESSAGE
          ) {
            flag = false
            errorFields.push(`additionalProperty_${index}_${key}`)
          }
      })
    })
  }
  console.log('errorFields', errorFields)
  return flag
}

/**
 * Drawn on Loan Structure from other inputs. The loan row never stores them, so an empty
 * check always fails even when the screen shows a value.
 */
const derivedLoanStructureFields = ['ltv', 'cltv', 'ltc', 'totalCombinedDebt', 'subjectPropertyMap']

const loanStructureFieldCountsAsData = (
  key: string,
  defaultFields: Record<string, any>,
  fieldProps?: Record<string, any>,
) => {
  if (derivedLoanStructureFields.includes(key)) return false
  const prop = fieldProps?.[key]
  const def = defaultFields[key]
  const required = prop?.required !== undefined ? !!prop.required : !!def?.required
  const readOnly = prop?.readOnly !== undefined ? !!prop.readOnly : !!def?.readOnly
  return required && !readOnly
}

export const validateLoanDetailCompletion = (loan: ILoan) => {
  let flag = true
  const visibleFields = visibleLoansLogic()
  const propertyVisibleFields: string[][] = []
  loan?.multipleProperties?.forEach((_, index) => {
    propertyVisibleFields.push(visiblePropertiesLogic(index, loan))
  })
  const { globalConfigReducer } = store.getState()
  const loanFieldProperties = globalConfigReducer[GlobalConfigType.LoanFields] as LoanFieldProps
  const fieldProps = loanFieldProperties?.[FieldOrigin.LoanStructure]
  const additionalFieldProps = getAdditionalPropertyFieldProps(loanFieldProperties)
  const defaultFields = getInitialLoanFields()
  const errorFields: string[] = []
  visibleFields.map((key) => {
    if (!loanStructureFieldCountsAsData(key, defaultFields, fieldProps)) return
    if (isEmpty((loan as any)[key])) {
      flag = false
      errorFields.push(key)
    }
  })
  if (loan.isMultipleProperties) {
    if (loan.howManyProperties != (loan?.multipleProperties?.length || 0) + 1) {
      flag = false
      errorFields.push('howManyProperties')
    }

    propertyVisibleFields.forEach((item, index) => {
      item.forEach((key) => {
        if (MultiPropertyFields.includes(key) && key !== 'estimatedDscr')
          if (
            loanStructureFieldCountsAsData(key, defaultFields, additionalFieldProps) &&
            isEmpty((loan.multipleProperties[index] as any)[key])
          ) {
            flag = false
            errorFields.push(`additionalProperty_${index}_${key}`)
          }
      })
    })
  }
  console.log('incomplete fields', errorFields)
  return flag
}

export const visibleOverrideFields = (fields: Record<string, InputType>, loan: ILoan) => {
  const newFields = cloneDeep(fields)
  if (!isNonDSCRProduct(loan.productType)) {
    delete newFields['rateSpread']
    delete newFields['exitFee']
  }
  if (newFields.lockDays.value === 'Other') {
    newFields.customLockDays.required = true
    newFields.customLockDays.visible = true
  } else {
    newFields.customLockDays.required = false
    newFields.customLockDays.visible = false
  }
  if (newFields.priceDisplay.value === 'Points') newFields.price.title = 'Points'
  else newFields.price.title = 'Price'

  return newFields
}

export const analyzeOverrideFields = (fields: Record<string, InputType>) => {
  let hasError = false
  let lockDaysError = false
  const newFields = cloneDeep(fields)
  const json: any = {}
  Object.keys(newFields).map((key) => {
    if (['override', 'note'].includes(key)) return
    json[key] = newFields[key].value
    newFields[key].error = InputValidate({ ...newFields[key] })
    if ((newFields as any)[key].error.length > 0) hasError = true
  })
  if (json.lockDays === 'Other' && Number(json.customLockDays) <= 0) lockDaysError = true

  json.sheetDate = `${formatDate('now')}(E)`
  if (json.lockDays === 'Other') json.lockDays = removeComma(json.customLockDays)
  if (json.priceDisplay === 'Points') json.price = solveDecimalJavascriptSum([100, -1 * removeComma(json.price)])
  delete json.customLockDays
  delete json.priceDisplay

  return { hasError, lockDaysError, json, newFields }
}

export const validatePriceAdjustment = (priceAdjustment: IPriceAdjustment, key: string, value: any) => {
  let flag = true
  if (!priceAdjustmentKeysWithMinMax.includes(key)) {
    if (!priceAdjustment) flag = false
  } else {
    if (!!priceAdjustment) {
      const { loan, loanDetail } = store.getState()
      const {
        value: estimatedClosingAmount,
        rehabFinancedPercent,
        totalRehabFinancedPercent,
      } = estimatedClosingAmountBorrower(loan, loanDetail.loanGlobalSettings)
      if (key === 'totalRehabFinancedPercent') value = totalRehabFinancedPercent
      else if (key === 'constructionFinancedHoldback') value = rehabFinancedPercent
      else if (key === 'atClosingBorrowerAmount') value = Math.abs(estimatedClosingAmount)
      else if (key === 'afterRepairValue') value = analyzeLoanStructureValue(loan).afterRepairValue
      else if (key === 'asIsValue') value = analyzeLoanStructureValue(loan).asIsValue
      else if (key === 'constructionReserve') value = analyzeLoanStructureValue(loan).constructionReserve

      if (!isEmpty(priceAdjustment?.min) && removeComma(priceAdjustment?.min ?? 0) > removeComma(value)) flag = false
      if (!isEmpty(priceAdjustment?.max) && removeComma(priceAdjustment?.max ?? 0) < removeComma(value)) flag = false
    } else flag = false
  }
  return flag
}
